import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import Switch from "react-switch";
import "react-phone-input-2/lib/style.css";
// css
import styles from "./addForm.module.scss";
import { FieldArray, Formik } from "formik";
import addTherapistFormSchema from "./AddTherapistForm.schema";
import LoadingButton from "components/shared/LoadingButton";
import {
  useCreateTherapistMutation,
  useGetTherapistDataQuery,
  useUpdateTherapistMutation,
} from "store/services/therapistProfiles";
import { useNavigate, useParams, Link } from "react-router-dom";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";
import { CLIENT_TYPE_OPTIONS, MIME_TYPES, TIMEZONE_OPTIONS } from "config/constants";
import { useDisciplineLookupQuery } from "store/services/disciplines";
import { cloneDeep, isEmpty } from "lodash";
import AppProfilePictureField from "components/shared/FormComponents/AppProfilePictureField";
import { successMessageToast } from "helpers/common";
import { THERAPIST_LIST } from "config/paths";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { useDesignationLookupQuery } from "store/services/designations";
import { Breadcrumbs, CommonMultiSelect, CommonSelect, DisciplineField, RequiredFieldAstrick } from "components/Common/Common";
import { genericFormErrorMsg } from "utils/validation/validationHelpers";
import AlertPop from "components/Modals/AlertPop";
import ViewAttachments from "pages/SideTabPages/ManageSupportStaff/TaskSessionView/ViewAttachments";
import { useLazyGetFileUrlQuery } from "store/services/utility";
import LoadingOverlay from "components/shared/LoadingOverlay";
import TextInput from "components/shared/FormComponents/V2/commonV2/TextInput";
import addTherapistFormSchemaV2 from "./AddTherapistForm.schemaV2 copy";
import { useSchoolDivisonLookupQuery } from "store/services/schoolsDivisons";
import CommonButton from "components/shared/FormComponents/V2/commonV2/CommonButton";
import { CloseIcon, RemoveIcon } from "Assets/icons";
import { CrossIcon } from "Assets/icons/V2";
import ChipSelect from "components/shared/FormComponents/V2/commonV2/ChipSelect";
import CheckboxMultiSelect from "components/shared/FormComponents/V2/commonV2/CheckboxMultiSelect";

const AddTherapistV2 = () => {
  const { id: therapistId } = useParams();
  const editMode = !!therapistId;
  const navigate = useNavigate();

  const {
    data: therapistData,
    isLoading: getTherapistLoading,
    isError: getTherapistError,
  } = useGetTherapistDataQuery(therapistId, { skip: !therapistId });

  const [createTherapist, { isLoading }] = useCreateTherapistMutation();
  const [updateTherapist, { isLoading: updateTherapistLoading }] = useUpdateTherapistMutation();
  const [getFileUrl, { isLoading: getFileLoading, isFetching: getFileFetching }] = useLazyGetFileUrlQuery();
  const { data: schoolDivisionsList, isLoading: schoolDivisionsListLoading } = useSchoolDivisonLookupQuery();

  const { data: disciplinesList } = useDisciplineLookupQuery({});
  const { data: designationsList } = useDesignationLookupQuery();

  const [isTherapistActive, setTherapistActive] = useState(false);
  const [allowDocUpload, setAllowDocUpload] = useState(true);

  const [showAlertPop, setShowAlertPop] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const initialValues = useMemo(() => {
    if (editMode && !therapistData) {
      return defaultInitialValues;
    }

    return ({
      ...defaultInitialValues,
      firstname: therapistData?.firstname,
      lastname: therapistData?.lastname,
      phone: therapistData?.phone,
      city: therapistData?.therapist_detail?.city || "",
      postal_code: therapistData?.therapist_detail?.postal_code || "",
      address1: therapistData?.therapist_detail?.address1 || "",
      address2: therapistData?.therapist_detail?.address2 || "",
      email: therapistData?.email,
      discipline_id: therapistData?.therapist_detail?.discipline_id ? {
        value: therapistData?.therapist_detail?.discipline_id,
        label: therapistData?.therapist_detail?.therapist_discipline?.name
      } : "",
      type: therapistData?.type || "veteran",
      designation_id: therapistData?.therapist_detail?.designation_id ? {
        value: therapistData?.therapist_detail?.designation_id,
        label: therapistData?.therapist_detail?.therapist_designation?.title
      } : "",
      registration_number: therapistData?.therapist_detail?.registration_number || "",
      insurance_policy_number: therapistData?.therapist_detail?.insurance_policy_number || "",
      assigned_client_category: therapistData?.therapist_detail?.assigned_client_category?.split(",")?.filter(Boolean).map(item => CLIENT_TYPE_OPTIONS?.find(x => x.value == item)),
      calendly_link: therapistData?.therapist_detail?.calendly_link || "",
      self_description: therapistData?.therapist_detail?.self_description || "",
      profile_picture: therapistData?.therapist_detail?.profile_picture || "",
      email_signature: therapistData?.therapist_detail?.email_signature || "",
      signature_img: therapistData?.therapist_detail?.signature_img || "",
      status: isTherapistActive ? "active" : "active",
      document_upload: allowDocUpload ? "allow" : "deny",
      documents: therapistData?.clientResources?.map(({ attachment_key, attachment_name, id }) => ({ attachment_key, attachment_name, id })) || [],
      timezone: { label: therapistData?.therapist_detail?.timezone, value: therapistData?.therapist_detail?.timezone },
      assign_schools:
        therapistData?.school_therapists?.map((item) => ({
          division: item.divisions
            ? { value: item.divisions.id, label: item.divisions.title }
            : null,

          name: item.schools
            ? { value: item.schools.id, label: item.schools.name }
            : null,
        })) || [{ division: null, name: null }],
      emergency_firstname: therapistData?.therapist_detail?.emergency_firstname || "",
      emergency_lastname: therapistData?.therapist_detail?.emergency_lastname || "",
      emergency_email: therapistData?.therapist_detail?.emergency_email || "",
      emergency_phone: therapistData?.therapist_detail?.emergency_phone || "",
      emergency_relationship: therapistData?.therapist_detail?.emergency_relationship || "",
    })
  }, [therapistData, isTherapistActive, allowDocUpload])

  useEffect(() => {
    if (!therapistData) {
      return;
    }
    setAllowDocUpload(therapistData?.document_upload === "allow");
    setTherapistActive(therapistData?.status === "active");
  }, [therapistData]);

  const handleTherapistStatus = (checked) => {
    setTherapistActive(checked);
  };

  const handleToggleDocUpload = (checked) => {
    setAllowDocUpload(checked);
  };

  const handleClickCancel = () => {
    navigate(THERAPIST_LIST);
  };

  const handleSubmit = async (payload) => {

    try {
      let result;
      const { emergency_firstname, emergency_lastname, emergency_email, emergency_phone, emergency_relationship, ...values } = cloneDeep(payload); values.email = values.email?.toLowerCase();
      values.designation_id = values.designation_id?.value;
      values.discipline_id = values.discipline_id?.value;
      values.insurance_policy_number = values.insurance_policy_number || "";
      values.registration_number = values.registration_number || "";
      values.timezone = values.timezone?.value;
      values.assigned_client_category = values.assigned_client_category
        ?.map(x => x.value)
        .join(",");
      values.assign_schools = values.assign_schools
        ? values.assign_schools
          .filter(item => item.division?.value || item.name?.value)
          .map(item => ({
            school_division_id: item.division?.value || null,
            school_id: item.name?.value || null,
          }))
        : [];

      if (editMode) {
        result = await updateTherapist({
          id: therapistId,
          payload: values,
        });
      } else {
        result = await createTherapist(values);
      }

      if (result.error) {
        console.error("Error creating Designation", result.error);
      } else {
        successMessageToast(result?.data?.message);
        navigate(THERAPIST_LIST);
      }
    } catch (err) {
      console.log("Error creating Designation", err);
    }
  };

  const handleAttachDocument = (name, value, prevValues, setFieldValue) => {
    try {
      let cloneValues = cloneDeep(prevValues);
      cloneValues.push({
        attachment_key: value,
        attachment_name: value,
      })
      setFieldValue(name, cloneValues);
    } catch (err) {
      console.error("Failed to attach document", err);
    }
  };

  const handleViewPdf = async (name) => {
    try {
      const url = await getFileUrl(name).unwrap();
      if (url) window.open(url, '_blank');

    } catch (err) { console.error("Error assigning therapist", err); }
  };

  const breadcrumbItems = [
    { label: "User", path: '/manage-team/therapist-profiles' },
    { label: editMode ? "Edit Clinician" : "Add Clinician" },
  ];

  return (
    <>
      <AlertPop
        confirmation={showAlertPop}
        setConfirmation={setShowAlertPop}
        message={alertMessage}
      />
      {
        getFileFetching && <LoadingOverlay />
      }
      <section className={`${styles.addForm} position-relative py-0 addForm`}>
        <Container fluid={true}>
          <Row
            style={{
              width: '100%',
              maxWidth: window.innerWidth >= 992 ? 'calc(100% - 170px)' : '100%',
              padding: '10px'
            }}
          >
            <Col lg={12} className="mt-2 mb-3">
              <div className="pageHead d-flex align-items-center justify-content-between flex-wrap">
                <Breadcrumbs themeBlueV2 items={breadcrumbItems} />
              </div>
              <div>
                <h3 className="h5 mt-3">{editMode ? "Edit Clinician" : "Add Clinician"}</h3>
              </div>
            </Col>

            <Row className="mt-0 g-3">
              {getTherapistLoading ? (
                <LoadingIndicator />
              ) : getTherapistError ? (
                <ErrorComponent />
              ) : (
                <Formik
                  initialValues={initialValues}
                  validationSchema={addTherapistFormSchemaV2}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    handleSubmit,
                    isSubmitting,
                    errors,
                    submitCount,
                    values,
                    validateForm,
                    setFieldValue
                  }) => (
                    <Form onSubmit={handleSubmit} className="mt-0">
                      <div>
                        <h3
                          className="h5 mb-0 text-uppercase fw-medium"
                          style={{ color: "#315d99", fontSize: "14px" }}
                        >
                          1. Clinician Information
                        </h3>
                        <div
                          className="py-2"
                          style={{ borderTop: "1px solid #315D99" }}
                        >

                          <Row className="g-3 mt-0">
                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="firstname"
                                label="First Name"
                                placeholder=""
                                required
                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="lastname"
                                label="Last Name"
                                required

                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="email"
                                label="Email"
                                required

                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="phone"
                                label="Phone Number"
                              />
                            </Col>
                          </Row>

                          <Row className="g-3 mt-3">
                            <Col md={6} sm={12} className="mt-0">
                              <TextInput
                                name="address1"
                                label="Address Line 1"
                              />
                            </Col>
                            <Col className="d-none d-lg-block mt-0" lg={3} md={6}></Col>

                            <Col lg={3} md={6} className="mt-0 reports-v2 my-3 d-flex justify-content-end classroom-conslutation">
                              <div className="w-100 align-items-center gap-3">
                                <label className="discharge-screen-label mb-0">Timezone<span>*</span></label>
                                <CommonSelect
                                  placeholder={"Choose one..."}
                                  name="timezone"
                                  option={TIMEZONE_OPTIONS}
                                  value={values.timezone}
                                  onChange={(val) => setFieldValue("timezone", val)}
                                  isFilterV2
                                  styles={{
                                    container: (provided) => ({
                                      ...provided,
                                      width: "100%",
                                    }),
                                  }}
                                />
                              </div>
                            </Col>
                          </Row>

                          <Row className="g-3 mt-3">
                            <Col lg={6} md={12} className="mt-0">
                              <TextInput
                                name="address2"
                                label="Address Line 2"
                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="city"
                                label="City"
                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="postal_code"
                                label="Postal Code"
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h3
                          className="h5 mb-0 text-uppercase fw-medium"
                          style={{ color: "#315d99", fontSize: "14px" }}
                        >
                          2. EMERGENCY CONTACT INFORMATION
                        </h3>
                        <div
                          className="py-2"
                          style={{ borderTop: "1px solid #315D99" }}
                        >
                          <div className="mb-4" style={{ width: "100%", backgroundColor: "#315D991A", color: '#315D99', fontStyle: "italic", borderRadius: '16px', padding: "6px", fontWeight: "400", textAlign: "center" }}>To be completed by the clinician</div>
                          <Row>
                            <Col md="6" className='d-flex gap-4'>
                              <TextInput
                                required
                                name="emergency_lastname"
                                label='Last Name'
                                disabled
                                value={values?.emergency_lastname}
                                onChange={(e) => setFieldValue("emergency_lastname", e.target.value)}
                              />
                              <TextInput
                                required
                                name="emergency_firstname"
                                label='First Name'
                                disabled
                                value={values?.emergency_firstname}
                                onChange={(e) => setFieldValue("emergency_firstname", e.target.value)}
                              />
                            </Col>
                            <Col md="3">
                              <TextInput
                                required
                                name="emergency_email"
                                label='Email' type='email'
                                disabled
                                value={values?.emergency_email}
                              // disabled
                              />
                            </Col>
                            <Col md="3">
                              <TextInput
                                required
                                name="emergency_phone"
                                label='Phone Number'
                                type='number'
                                disabled
                                value={values?.emergency_phone}
                                onChange={(e) => setFieldValue("emergency_phone", e.target.value)}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col md="4">
                              <TextInput
                                name="emergency_relationship"
                                label='Relationship to Clinician'
                                disabled
                                value={values?.emergency_relationship}
                                onChange={(e) => setFieldValue("emergency_relationship", e.target.value)}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h3
                          className="h5 mb-0  text-uppercase fw-medium"
                          style={{ color: "#315d99", fontSize: "14px" }}
                        >
                          2. KCS INFORMATION
                        </h3>

                        <div
                          className="py-3"
                          style={{ borderTop: "1px solid #315D99" }}
                        >
                          <Row className="g-3">

                            <Col lg={12} md={12}>
                              <div className="d-flex gap-2 flex-wrap">
                                <ChipSelect
                                  name="discipline_id"
                                  label="Discipline"
                                  required
                                  options={disciplinesList}
                                />
                              </div>
                            </Col>
                          </Row>

                          <Row className="g-3 mt-1 align-items-start">
                            <Col lg={4} md={6} className="reports-v2 my-3  classroom-conslutation">
                              <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                Designation<span>*</span>
                              </label>
                              <CommonSelect
                                placeholder=""
                                name="designation_id"
                                option={
                                  designationsList?.length && designationsList?.map((item) => ({
                                    value: item.id,
                                    label: item.title,
                                  }))
                                }
                                isFilterV2
                              />
                            </Col>

                            <Col lg={4} md={6} className="reports-v2 my-3  classroom-conslutation">
                              <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                Type<span>*</span>
                              </label>

                              <CommonSelect
                                placeholder=""
                                name="type"
                                isFilterV2
                                value={values.type ? { label: values.type, value: values.type } : null}
                                onChange={(val) => setFieldValue("type", val?.value || "")}

                                option={[
                                  { label: "Veteran", value: "veteran" },
                                  { label: "Apprentice", value: "apprentice" },
                                ]}
                                styles={{
                                  container: (provided) => ({
                                    ...provided,
                                    width: "100%",
                                  }),
                                }}
                              />
                            </Col>

                            <Col lg={4} md={12}>
                              <CheckboxMultiSelect
                                name="assigned_client_category"
                                label="Client Type"
                                required
                                options={CLIENT_TYPE_OPTIONS}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h3 className="h5 mb-0 text-uppercase fw-medium" style={{ color: "#315d99", fontSize: "14px" }}>
                          3.   ASSIGN SCHOOLS FOR CLASSROOM REPORTS
                        </h3>
                        <div style={{ borderTop: "1px solid #315D99" }}>
                          <FieldArray name="assign_schools" >
                            {({ push, remove }) => (
                              <>
                                {values.assign_schools?.map((_, index) => (
                                  <Row className="g-3 align-items-end reports-v2 my-1  classroom-conslutation" key={index}>
                                    <div className="d-flex gap-3 align-items-end">
                                      <div className="w-100">
                                        <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                          School Division
                                        </label>
                                        <CommonSelect
                                          placeholder=""
                                          value={values.assign_schools[index].division}
                                          option={
                                            schoolDivisionsList?.length && schoolDivisionsList?.map(item => ({
                                              value: item.id,
                                              label: item.title,
                                            }))
                                          }
                                          onChange={(val) => setFieldValue(`assign_schools[${index}].division`, val)}
                                          isClearable
                                          isFilterV2

                                        />
                                      </div>
                                      <div className="w-100">
                                        <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                          School Name
                                        </label>
                                        <CommonSelect
                                          placeholder=""
                                          value={values.assign_schools[index].name}
                                          onChange={(val) => setFieldValue(`assign_schools[${index}].name`, val)}
                                          isClearable
                                          isFilterV2
                                          option={(() => {
                                            const divisionId = values.assign_schools[index].division?.value;

                                            if (!divisionId) return [];

                                            const selectedSchoolIds = values?.assign_schools?.filter(
                                              (item) => item?.division?.value === divisionId
                                            ).map((item) => item?.name?.value)

                                            const divisionObj = schoolDivisionsList?.find(d => d.id === divisionId);

                                            if (!divisionObj?.schools?.length) return [];

                                            return cloneDeep(divisionObj.schools)
                                              .filter(
                                                (school) =>
                                                  !selectedSchoolIds.includes(school.id)
                                              )
                                              .sort((a, b) => a.name.localeCompare(b.name))
                                              .map((school) => ({
                                                value: school.id,
                                                label: school.name,
                                              }));
                                          })()}
                                        />
                                      </div>

                                      <div style={{ width: "24px" }}>
                                        {index > 0 ? (
                                          <Button
                                            onClick={() => remove(index)}
                                            variant="link"
                                            className="p-0 border-0 mb-2"
                                          >
                                            <CrossIcon classes="text-danger" />
                                          </Button>
                                        ) : null}
                                      </div>

                                    </div>
                                  </Row>
                                ))}

                                <CommonButton
                                  className="mt-4"
                                  text="Add Another"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    push({ division: null, name: null })
                                  }} />

                              </>
                            )}
                          </FieldArray>
                        </div>
                      </div>

                      {!isEmpty(errors) && submitCount > 0 && (
                        <Row className="mt-4">
                          <Col lg="12">
                            <span className="error">{genericFormErrorMsg}</span>
                          </Col>
                        </Row>
                      )}

                      <Row style={{ marginTop: "55px" }}>
                        <Col>
                          <div style={{ background: "#315D991A", textAlign: "center", padding: "27px", borderRadius: "12px" }}>
                            <p style={{ color: "#315D99", margin: 0 }}>Account will become active when the user accepts the KCS Policies.</p>
                          </div>
                        </Col>
                        <Col lg="12">
                          <div
                            className={`${styles.btnWrpper} d-flex align-items-center justify-content-between gap-10 pt-3`}

                          >
                            <CommonButton text="Cancel" onClick={handleClickCancel} />
                            <CommonButton
                              type="submit"
                              text="Done"
                              isLoading={isSubmitting}
                              style={{ color: "#fff", background: "#315D99" }}
                              onClick={() => {
                                validateForm().then((validationErrors) => {
                                  const fieldOrder = [
                                    "firstName",
                                    "lastName",
                                    "email",
                                    "phone",
                                    "addressLine1",
                                    "state",
                                    "addressLine2",
                                    "city",
                                    "postalCode",
                                  ];

                                  const errorKey = Object.keys(validationErrors || {})
                                    .sort(
                                      (a, b) =>
                                        fieldOrder.indexOf(a) -
                                        fieldOrder.indexOf(b)
                                    )[0];

                                  if (errorKey) {
                                    const el = document.querySelector(
                                      `[name="${errorKey}"]`
                                    );
                                    el?.scrollIntoView({
                                      behavior: "smooth",
                                      block: "center",
                                    });
                                    el?.focus();
                                  }
                                });
                              }}
                            />
                          </div>
                        </Col>
                      </Row>

                      {console.log("Formik Values:", values)}
                    </Form>
                  )}
                </Formik>
              )}
            </Row>
          </Row>
        </Container>
      </section>
    </>
  );
};

const defaultInitialValues = {
  firstname: "",
  lastname: "",
  phone: "",
  email: "",
  discipline_id: null,
  type: "veteran",
  designation_id: null,
  registration_number: "",
  insurance_policy_number: "",
  assigned_client_category: [],
  calendly_link: "",
  self_description: "",
  profile_picture: "",
  email_signature: "",
  signature_img: "",
  status: "active",
  document_upload: "allow",
  documents: [],
  assign_schools: []
};

export default AddTherapistV2;
